/**
 * DataTableWrapper - Wrapper component for expandable data tables
 *
 * Provides two display modes:
 * 1. Collapsed: Shows first N rows (default 10) with "Show All" button (bottom right)
 * 2. Expanded: Scrollable table sized to visible area
 *
 * Usage:
 * ```tsx
 * <DataTableWrapper totalRows={data.length} collapsedRowCount={10}>
 *   <ReusableTable columns={columns} data={displayData} />
 * </DataTableWrapper>
 * ```
 *
 * Note: The component expects the child table to receive the appropriately sliced data.
 * Use React.cloneElement or render props pattern to pass limited data when collapsed.
 */

import { Box, Button, HStack, type TableRowProps, Text } from '@chakra-ui/react';
import type React from 'react';
import { cloneElement, isValidElement, useMemo, useState } from 'react';

import { LAYOUT_SPACING } from '~/components/layout/shared/spacing';
import type { ColumnDefinition } from '~/components/tables/ReusableTable';

interface DataTableWrapperProps<T = unknown> {
  /** Table content to wrap (should be ReusableTable or similar) */
  children: React.ReactElement<{
    data: T[];
    footerRow?: T;
    footerRowStyle?: TableRowProps;
    columns?: unknown[];
  }>;

  /** Total number of rows in the full dataset */
  totalRows: number;

  /** Number of rows to show in collapsed mode (default: LAYOUT_SPACING.TABLE_DEFAULT_ROWS) */
  collapsedRowCount?: number;

  /** Maximum height for expanded mode (default: 600px) */
  maxHeight?: string;

  /** Where to render the expand/collapse button (default: "footer") */
  buttonPlacement?: 'header' | 'footer' | 'both';

  /** Render function for header content (receives expand state and toggle function) */
  renderHeader?: (isExpanded: boolean, toggle: () => void) => React.ReactNode;
}

export const DataTableWrapper = <
  T extends React.JSXElementConstructor<React.ReactNode> | keyof React.JSX.IntrinsicElements,
>({
  children,
  totalRows,
  collapsedRowCount = LAYOUT_SPACING.TABLE_DEFAULT_ROWS,
  buttonPlacement = 'footer',
  renderHeader,
}: DataTableWrapperProps<T>):
  | React.ReactElement<React.ComponentProps<React.JSXElementConstructor<T>>>
  | React.ReactElement<React.ComponentProps<keyof React.JSX.IntrinsicElements>> => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  // If total rows is less than or equal to collapsed count, always show all
  const shouldShowExpandButton = totalRows > collapsedRowCount;

  // Toggle function for external use
  const toggleExpanded = () => setIsExpanded(!isExpanded);

  // Get the data from the child component
  const childData = children.props.data || [];
  const displayData = useMemo(() => {
    if (!shouldShowExpandButton || isExpanded) {
      return childData;
    }
    return childData.slice(0, collapsedRowCount);
  }, [childData, shouldShowExpandButton, isExpanded, collapsedRowCount]);

  // Extract footer info if present
  const hasFooter = isValidElement(children) && 'footerRow' in children.props;
  const footerRowData =
    hasFooter && 'footerRow' in children.props ? (children.props.footerRow as T | undefined) : null;
  const footerRowStyle =
    hasFooter && 'footerRowStyle' in children.props ? children.props.footerRowStyle : null;
  const tableColumns = hasFooter && 'columns' in children.props ? children.props.columns : null;

  // Clone the child element with limited data when collapsed
  // When expanded, remove the footerRow to render it separately below (sticky footer)
  const tableElement = useMemo(() => {
    if (isValidElement(children)) {
      const props: Partial<{
        data: T[];
        footerRow?: T;
        footerRowStyle?: TableRowProps;
        columns?: unknown[];
      }> = {
        data: displayData as T[],
      };
      // When expanded, remove footer from table so we can render it separately (sticky)
      if (isExpanded && shouldShowExpandButton && hasFooter) {
        props.footerRow = undefined;
      }
      return cloneElement(children, props);
    }
    return children;
  }, [children, displayData, isExpanded, shouldShowExpandButton, hasFooter]);

  // Render expand/collapse button
  const expandButton = shouldShowExpandButton && (
    <Button
      size="sm"
      variant="outline"
      onClick={toggleExpanded}
      colorPalette="blue"
      transition={`all ${LAYOUT_SPACING.TRANSITION_FAST}`}
    >
      {isExpanded ? `Show Less (${collapsedRowCount} rows)` : `Show All (${totalRows} rows)`}
    </Button>
  );

  // Render sticky footer when expanded
  const renderStickyFooter = () => {
    if (!isExpanded || !shouldShowExpandButton || !hasFooter || !tableColumns) return null;

    // Position above the sticky button (button height ~48px with padding)
    const stickyButtonHeight =
      buttonPlacement === 'footer' || buttonPlacement === 'both' ? '52px' : '0px';

    return (
      <Box
        bg={footerRowStyle?.bg || 'white'}
        borderTop="2px solid"
        borderTopColor={footerRowStyle?.borderTopColor || 'gray.300'}
        fontWeight={footerRowStyle?.fontWeight || 'bold'}
        position="sticky"
        bottom={stickyButtonHeight}
        zIndex={1}
        flexShrink={0}
      >
        <HStack gap={0} px={2}>
          {Array.isArray(tableColumns) &&
            tableColumns.map((col: unknown) => {
              const column = col as ColumnDefinition<Record<string, unknown>>;
              let cellContent: React.ReactNode = '';
              if (footerRowData !== null && footerRowData !== undefined) {
                if (typeof column.accessor === 'function') {
                  cellContent = (column.accessor as (item: T) => React.ReactNode)(footerRowData);
                } else if (typeof footerRowData === 'object' && column.key in footerRowData) {
                  cellContent = (footerRowData as Record<string, unknown>)[
                    column.key
                  ] as React.ReactNode;
                }
              }
              return (
                <Box
                  key={`sticky-footer-${column.key}`}
                  flex={1}
                  textAlign={(column.cellTextAlign || 'start') as 'start' | 'center' | 'end'}
                  py={3}
                  px={3}
                >
                  {cellContent}
                </Box>
              );
            })}
        </HStack>
      </Box>
    );
  };

  return (
    <Box height="100%" display="flex" flexDirection="column">
      {/* Custom header if provided */}
      {renderHeader && <Box flexShrink={0}>{renderHeader(isExpanded, toggleExpanded)}</Box>}

      {/* Table content - scrollable when expanded */}
      <Box
        flex={1}
        overflowY={isExpanded ? 'auto' : undefined}
        overflowX="auto"
        minHeight={0}
        position="relative"
      >
        {tableElement}

        {/* Sticky footer when expanded - inside scroll area */}
        {renderStickyFooter()}

        {/* Sticky button when expanded - inside scroll area at bottom of viewport */}
        {isExpanded &&
          shouldShowExpandButton &&
          (buttonPlacement === 'footer' || buttonPlacement === 'both') && (
            <Box
              position="sticky"
              bottom={0}
              bg="white"
              borderTop="1px solid"
              borderColor="gray.200"
              py={2}
              px={3}
              zIndex={2}
              mt={LAYOUT_SPACING.ELEMENT_GAP}
            >
              <HStack justify="flex-end">{expandButton}</HStack>
            </Box>
          )}
      </Box>

      {/* Button when collapsed - outside scroll area */}
      {!isExpanded &&
        shouldShowExpandButton &&
        (buttonPlacement === 'footer' || buttonPlacement === 'both') && (
          <HStack
            justify="flex-end"
            mt={LAYOUT_SPACING.ELEMENT_GAP}
            pt={LAYOUT_SPACING.ELEMENT_GAP}
            flexShrink={0}
          >
            {expandButton}
          </HStack>
        )}

      {/* Row count when no expand button needed */}
      {!shouldShowExpandButton && totalRows > 0 && (
        <Text mt={LAYOUT_SPACING.ELEMENT_GAP} fontSize="sm" color="gray.600" textAlign="right">
          {totalRows} row{totalRows !== 1 ? 's' : ''}
        </Text>
      )}
    </Box>
  );
};
